/* App Guide — content. Each topic: steps with a tappable mock of the real screen. */
const GUIDE_TRACKS = [
  { id:'new',   label:'Just unboxed',  sub:'Set up your first tracker' },
  { id:'more',  label:'Adding more',   sub:'A second vehicle or another person' },
  { id:'fleet', label:'Fleet setup',   sub:'Running a team of drivers' },
];

const GUIDE_TOPICS = [
/* ---------------------------------------------------------------- 1 */
{ id:'adddevice', title:'Activate a new tracker', ic:'plus', cat:'Getting started',
  tracks:['new','more'], go:'adddevice', mins:'3 min',
  sum:'Enter the registration code and PIN from the box to pair the tracker with your account.',
  kw:'activate pair setup registration code pin imei serial new device tracker box register scan qr',
  intro:"Everything starts here. Have the tracker — or the card from the box — in hand: you'll need the Registration Code and the PIN. Nothing you tap in this walkthrough is permanent, so feel free to poke around.",
  steps:[
    { t:'Open the menu, then Add device', b:'Tap the menu button in the top-left of any screen. “Add device” sits right under your vehicle list.', hint:'Tap “Add device”',
      mock:{ title:'Menu', items:[
        {k:'row', ic:'car', t:'Sedan 640', s:'BMW 2-Series · Parked'},
        {k:'row', ic:'car', t:'Shop Van', s:'Ford Transit · Moving'},
        {k:'row', ic:'plus', t:'Add device', s:'Activate a new tracker', hot:true},
      ]}},
    { t:'Enter the registration code and PIN', b:'Both numbers are printed on the card in the device box — and on the label on the back of the tracker. Type them exactly, or tap Scan QR code to skip the typing.', hint:'Tap the Registration Code field',
      mock:{ title:'Set Up a Device', back:true, items:[
        {k:'field', l:'Registration Code', ph:'8490729', hot:true},
        {k:'field', l:'Pin', ph:'9419875'},
        {k:'note', t:'*Registration Code and Pin can be found in the device box.'},
        {k:'btn', l:'Scan QR Code'},
        {k:'btn', l:'Next', kind:'primary'},
      ]}},
    { t:'Name it something you’ll recognize', b:'“Shop Van” beats “Device 4” when an alert lands at midnight. You can rename it any time from the vehicle screen.', hint:'Tap Nickname',
      mock:{ title:'Name this device', back:true, items:[
        {k:'field', l:'Nickname', ph:'Shop Van', hot:true},
        {k:'field', l:'Category', v:'Internal Combustion Engine Vehicle'},
        {k:'btn', l:'Save & continue', kind:'primary'},
      ]}},
    { t:'Plug it in and wait for the first ping', b:'Power the tracker, then park somewhere with open sky. The first location usually arrives within two minutes — the dot turns green when it does.', hint:'Tap Finish',
      mock:{ title:'Almost there', back:true, items:[
        {k:'map', label:'Waiting for first location…'},
        {k:'stat', l:'Signal', v:'Searching'},
        {k:'btn', l:'Finish', kind:'primary', hot:true},
      ]}},
  ],
  end:"That’s it — the device is yours for life, no monthly fee. It’ll show up on your map alongside everything else."
},
/* ---------------------------------------------------------------- 2 */
{ id:'geofence', title:'Create a geofence', ic:'pin', cat:'Alerts & zones',
  tracks:['new','fleet'], go:'geofences', mins:'4 min',
  sum:'Draw a zone on the map and get told the moment a vehicle enters or leaves it.',
  kw:'geofence zone fence boundary area radius enter exit arrive leave home depot school',
  intro:"A geofence is just a shape on a map with a rule attached. Most people start with one around home and one around work — then you stop wondering whether the car made it.",
  steps:[
    { t:'Open Geofences', b:'From the menu, choose Geofences. Any zones you’ve already drawn are listed here with a switch to pause them.', hint:'Tap Geofences',
      mock:{ title:'Menu', items:[
        {k:'row', ic:'map', t:'Live Map', s:'All 26 devices'},
        {k:'row', ic:'pin', t:'Geofences', s:'12 active zones', hot:true},
        {k:'row', ic:'bell', t:'Alerts', s:'3 unread'},
      ]}},
    { t:'Tap the plus to start a new zone', b:'The button in the bottom-right adds a zone. Nothing saves until you confirm, so it’s safe to experiment.', hint:'Tap the + button',
      mock:{ title:'Geofences', items:[
        {k:'row', ic:'pin', t:'Home', s:'250 m radius · Entry & exit'},
        {k:'row', ic:'grid', t:'Main Depot', s:'Polygon · Entry only'},
        {k:'fab', hot:true},
      ]}},
    { t:'Drop it on the map and set the size', b:'Search an address or drag the map, then widen the circle until it covers the property. A radius of 150–300 m suits most driveways without false alarms.', hint:'Tap the map',
      mock:{ title:'New geofence', back:true, items:[
        {k:'map', label:'265 Elm Street, Millbrook', circle:true, hot:true},
        {k:'field', l:'Radius', v:'250 m'},
      ]}},
    { t:'Choose what triggers an alert', b:'Entry, exit, or both. If a zone is somewhere a vehicle sits all day, pick one direction only — you’ll get half the notifications and none of the noise.', hint:'Tap the Exit switch',
      mock:{ title:'New geofence', back:true, items:[
        {k:'field', l:'Name', v:'Home'},
        {k:'toggle', l:'Alert on entry', s:'When a vehicle arrives', on:true},
        {k:'toggle', l:'Alert on exit', s:'When a vehicle leaves', on:false, hot:true},
      ]}},
    { t:'Pick who it watches, then save', b:'Apply the zone to one vehicle or all of them. Save, and it’s live immediately — no need to restart anything.', hint:'Tap Save geofence',
      mock:{ title:'New geofence', back:true, items:[
        {k:'field', l:'Applies to', v:'All 26 devices'},
        {k:'btn', l:'Save geofence', kind:'primary', hot:true},
      ]}},
  ],
  end:"Your zone is armed. Alerts from it appear under Alerts, and as a push notification if you have those switched on."
},
/* ---------------------------------------------------------------- 3 */
{ id:'speeding', title:'Turn on speeding alerts', ic:'speed', cat:'Alerts & zones',
  tracks:['new','fleet'], go:'settings', mins:'3 min',
  sum:'Set a speed limit per vehicle and get a notification when it’s crossed.',
  kw:'speeding speed alert limit mph kph fast driving harsh braking teen driver',
  intro:"Speed alerts are per-vehicle, so a work truck and a teenager’s car can have very different rules. Start a little above the posted limit — 5–10 over — or you’ll hear from it constantly.",
  steps:[
    { t:'Go to Settings → Notifications', b:'Open Settings from the menu, then Push notifications. This is the master list of what the app is allowed to tell you.', hint:'Tap Push notifications',
      mock:{ title:'Settings', items:[
        {k:'row', ic:'bell', t:'Push notifications', s:'What reaches your phone', hot:true},
        {k:'row', ic:'doc', t:'Email notifications', s:'Summaries & reports'},
        {k:'row', ic:'map', t:'Map preferences', s:'Units, layers, updates'},
      ]}},
    { t:'Switch on Speeding', b:'Find Speeding under the Driving group and turn it on. This is the switch that decides whether the alert reaches your phone at all.', hint:'Tap the Speeding switch',
      mock:{ title:'Push notifications', back:true, items:[
        {k:'toggle', l:'Harsh braking', s:'Sudden stops', on:true},
        {k:'toggle', l:'Speeding', s:'Over the set limit', on:false, hot:true},
        {k:'toggle', l:'Rapid acceleration', s:'Hard starts', on:true},
      ]}},
    { t:'Set the threshold on the vehicle', b:'Open the vehicle, tap Edit device, and set its speed limit. Each vehicle keeps its own number.', hint:'Tap Speed limit',
      mock:{ title:'Edit device', back:true, items:[
        {k:'field', l:'Nickname', v:'Sedan 640'},
        {k:'field', l:'Speed limit alert', v:'70 mph', hot:true},
        {k:'note', t:'Tip: set it 5–10 over the roads you actually drive.'},
      ]}},
    { t:'Save, and check it under Alerts', b:'Every speeding event lands in the Alerts list with the time, place and top speed — handy later even if you dismissed the notification.', hint:'Tap Save',
      mock:{ title:'Edit device', back:true, items:[
        {k:'stat', l:'Speed limit alert', v:'70 mph'},
        {k:'btn', l:'Save changes', kind:'primary', hot:true},
      ]}},
  ],
  end:"Done. If alerts feel too frequent, raise the number by 5 rather than switching it off — you’ll keep the ones that matter."
},
/* ---------------------------------------------------------------- 4 */
{ id:'users', title:'Add someone to your account', ic:'users', cat:'People & access',
  tracks:['more','fleet'], go:'users', mins:'3 min',
  sum:'Invite a partner, a driver or a manager, and choose exactly what they can see.',
  kw:'users add user invite share access permissions role admin viewer driver family partner spouse',
  intro:"Sharing doesn’t mean giving away control. Roles decide what someone can do, and you can change or revoke access at any moment without touching the device.",
  steps:[
    { t:'Open Users', b:'From the menu under Manage, tap Users. You’ll see everyone with access, plus invitations still waiting to be accepted.', hint:'Tap Users',
      mock:{ title:'Menu', items:[
        {k:'row', ic:'users', t:'Users', s:'Access & sharing', hot:true},
        {k:'row', ic:'chart', t:'Reports', s:'Export & schedule'},
        {k:'row', ic:'cog', t:'Settings', s:'Account & preferences'},
      ]}},
    { t:'Tap Invite user', b:'You’ll need their email address — the invitation goes there, and they create their own password. Never share your own login.', hint:'Tap Invite user',
      mock:{ title:'Users', items:[
        {k:'tabs', opts:['Users','Sent','Mine'], sel:0},
        {k:'row', ic:'users', t:'Marcus Reed', s:'Owner · full access'},
        {k:'row', ic:'users', t:'Dana Whitley', s:'Administrator'},
        {k:'btn', l:'Invite user', kind:'primary', hot:true},
      ]}},
    { t:'Pick their role', b:'Viewer sees location and history. Driver sees only their own vehicles. Administrator can change settings and devices. Owner is you, and stays you.', hint:'Tap Viewer',
      mock:{ title:'Invite user', back:true, items:[
        {k:'field', l:'Email', v:'sam.reed@gmail.com'},
        {k:'chips', opts:['Administrator','Viewer','Driver'], sel:1, hot:true},
        {k:'note', t:'Viewer: sees location and trips, changes nothing.'},
      ]}},
    { t:'Choose their vehicles and send', b:'Give access to everything, or just the vehicles they need. The invite expires in seven days if it isn’t accepted.', hint:'Tap Send invitation',
      mock:{ title:'Invite user', back:true, items:[
        {k:'field', l:'Vehicles', v:'Shop Van, Truck 883'},
        {k:'btn', l:'Send invitation', kind:'primary', hot:true},
      ]}},
  ],
  end:"They’ll get an email straight away. Until they accept, the invite sits under the Sent tab where you can cancel it."
},
/* ---------------------------------------------------------------- 5 */
{ id:'notifs', title:'Choose which alerts reach you', ic:'bell', cat:'Alerts & zones',
  tracks:['new','more'], go:'settings', mins:'2 min',
  sum:'Separate the urgent from the interesting — push on your phone, email for the rest.',
  kw:'notifications push email alerts quiet mute summary preferences noise',
  intro:"Most people turn everything on, get overwhelmed in a week, then turn everything off. A better plan: push for things that need you now, email for things you’ll read on Sunday.",
  steps:[
    { t:'Open Settings', b:'Settings lives at the bottom of the More screen and in the menu. Notifications are split into push and email, on purpose.', hint:'Tap Settings',
      mock:{ title:'More', items:[
        {k:'row', ic:'help', t:'App guide', s:'Get the most from Moto Watchdog'},
        {k:'row', ic:'cog', t:'Settings', s:'Account & preferences', hot:true},
      ]}},
    { t:'Push: keep the urgent handful', b:'Unauthorized movement, tow detection, geofence exit, low device battery. That’s a good starting set — everything else can wait.', hint:'Tap the Tow detected switch',
      mock:{ title:'Push notifications', back:true, items:[
        {k:'toggle', l:'Unauthorised movement', s:'Vehicle moves with engine off', on:true},
        {k:'toggle', l:'Tow detected', s:'Lifted or towed', on:false, hot:true},
        {k:'toggle', l:'Trip started', s:'Every time it moves', on:true},
      ]}},
    { t:'Email: the round-ups', b:'Trip summaries and weekly reports read much better in an inbox than as a buzz in your pocket. Switch those over here.', hint:'Tap the Trip summary switch',
      mock:{ title:'Email notifications', back:true, items:[
        {k:'toggle', l:'Trip summary', s:'Daily digest', on:false, hot:true},
        {k:'toggle', l:'Device low battery', s:'Under 20%', on:true},
      ]}},
  ],
  end:"Changes save as you flip them. If your phone still stays quiet, check that Moto Watchdog has notification permission in your phone’s own settings."
},
/* ---------------------------------------------------------------- 6 */
{ id:'maint', title:'Set a maintenance reminder', ic:'wrench', cat:'Everyday use',
  tracks:['more','fleet'], go:'maintenance', mins:'3 min',
  sum:'Oil, tyres, inspection — reminders based on real mileage, not guesswork.',
  kw:'maintenance service reminder oil change mileage interval inspection tires dtc engine light',
  intro:"Because the tracker reads actual mileage, a reminder set at 5,000 miles fires when the vehicle has really driven 5,000 miles — not when the calendar says it might have.",
  steps:[
    { t:'Open Maintenance', b:'From the menu under Manage. Anything the engine has reported — including check-engine codes — is waiting here too.', hint:'Tap Maintenance',
      mock:{ title:'Menu', items:[
        {k:'row', ic:'wrench', t:'Maintenance', s:'2 items need attention', hot:true},
        {k:'row', ic:'fuel', t:'Fuel', s:'Expenses by period'},
      ]}},
    { t:'Add a reminder', b:'Choose the vehicle, then the job — oil change, tyre rotation, registration, or something you name yourself.', hint:'Tap Add reminder',
      mock:{ title:'Maintenance', items:[
        {k:'tabs', opts:['Unresolved','Resolved'], sel:0},
        {k:'row', ic:'engine', t:'P1031 · Oxygen sensor', s:'Sedan 640 · High severity'},
        {k:'btn', l:'Add reminder', kind:'primary', hot:true},
      ]}},
    { t:'Set the interval', b:'Every so many miles, or on a date — or both, whichever comes first. That last option is the one most workshops recommend.', hint:'Tap Every',
      mock:{ title:'New reminder', back:true, items:[
        {k:'field', l:'Task', v:'Oil change'},
        {k:'field', l:'Every', v:'5,000 mi or 6 months', hot:true},
        {k:'note', t:'Current odometer: 48,204 mi'},
      ]}},
    { t:'Save and forget it', b:'You’ll be nudged as the mileage approaches, and again when it’s due. Mark it done and the next interval starts automatically.', hint:'Tap Save reminder',
      mock:{ title:'New reminder', back:true, items:[
        {k:'stat', l:'Next due', v:'53,204 mi'},
        {k:'btn', l:'Save reminder', kind:'primary', hot:true},
      ]}},
  ],
  end:"Reminders live on the vehicle screen under Maintenance log, along with everything you’ve already done."
},
/* ---------------------------------------------------------------- 7 */
{ id:'fuel', title:'Log fuel and see what it costs', ic:'fuel', cat:'Reports & records',
  tracks:['more','fleet'], go:'fuel', mins:'2 min',
  sum:'Add fill-ups as you go and the app works out cost per mile for you.',
  kw:'fuel gas fill up mpg cost expense receipt gallons litres economy spending',
  intro:"Two taps at the pump is all this takes, and after a month you’ll know exactly what each vehicle costs to run. Snap the receipt if you want a record for taxes.",
  steps:[
    { t:'Open Fuel', b:'It’s on the More screen and in the menu. The top of the screen shows spending for the period you pick.', hint:'Tap Fuel',
      mock:{ title:'More', items:[
        {k:'row', ic:'fuel', t:'Fuel', s:'Expenses by period', hot:true},
        {k:'row', ic:'chart', t:'Reports', s:'Export & schedule'},
      ]}},
    { t:'Add a fill-up', b:'Pick the vehicle, then enter gallons and what you paid. Odometer is optional — but filling it in is what makes the MPG figure accurate.', hint:'Tap Add fill-up',
      mock:{ title:'Fuel', items:[
        {k:'seg', opts:['Week','Month','Year'], sel:1},
        {k:'stat', l:'This month', v:'$412.80'},
        {k:'stat', l:'Cost per mile', v:'$0.21'},
        {k:'btn', l:'Add fill-up', kind:'primary', hot:true},
      ]}},
    { t:'Enter the numbers', b:'Gallons and total cost. The app fills in the date and finds the station from the vehicle’s location at the time.', hint:'Tap Gallons',
      mock:{ title:'Add fill-up', back:true, items:[
        {k:'field', l:'Vehicle', v:'Shop Van'},
        {k:'field', l:'Gallons', ph:'14.2', hot:true},
        {k:'field', l:'Total cost', ph:'$48.60'},
        {k:'btn', l:'Save', kind:'primary'},
      ]}},
  ],
  end:"Two or three fill-ups in, the cost-per-mile figure settles down and becomes genuinely useful."
},
/* ---------------------------------------------------------------- 8 */
{ id:'trips', title:'Watch a trip as it happens', ic:'route', cat:'Everyday use',
  tracks:['new','fleet'], go:'trips', mins:'2 min',
  sum:'Follow a vehicle live, then read the full history of where it’s been.',
  kw:'trips live tracking history route replay playback where is my car following journey',
  intro:"When a vehicle is moving, the app follows it in near real time. When it stops, the trip is filed away with its route, distance and top speed — going back months.",
  steps:[
    { t:'Tap the live banner', b:'Any trip in progress shows a green LIVE banner on the map and vehicle screens. Tap it to follow along.', hint:'Tap the LIVE banner',
      mock:{ title:'Live Map', items:[
        {k:'map', label:'Shop Van · moving · 34 mph'},
        {k:'row', ic:'bolt', t:'Trip in progress', s:'Started 8:07 PM · 12.4 mi', hot:true},
      ]}},
    { t:'Follow the route', b:'The line draws itself as new positions arrive, usually every few seconds. Distance and duration tick up alongside it.', hint:'Tap the map',
      mock:{ title:'Live trip', back:true, items:[
        {k:'map', label:'Following Shop Van', circle:false, hot:true},
        {k:'stat', l:'Distance', v:'12.4 mi'},
        {k:'stat', l:'Elapsed', v:'23m 30s'},
      ]}},
    { t:'Open a finished trip', b:'Under Trips, every journey is listed by day. Tap one to replay the route, see stops, and check the top speed against the road’s limit.', hint:'Tap a trip',
      mock:{ title:'Trips', items:[
        {k:'row', ic:'route', t:'North Yard → 265 Elm Street', s:'24.78 mi · 50 min · Today', hot:true},
        {k:'row', ic:'route', t:'265 Elm Street → North Yard', s:'0.18 mi · 4 min · Today'},
      ]}},
  ],
  end:"Trips can be renamed and tagged as business or personal, which is what makes the mileage report worth running at tax time."
},
/* ---------------------------------------------------------------- 9 */
{ id:'reports', title:'Build and schedule a report', ic:'chart', cat:'Reports & records',
  tracks:['fleet'], go:'reports', mins:'3 min',
  sum:'Mileage, driving scores or speeding violations — as a one-off or every Monday.',
  kw:'report export csv pdf mileage ifta tax driving score speeding violations schedule email',
  intro:"Reports pull from data the tracker has already collected, so there’s nothing to set up in advance. Pick a type, a date range, and where it should land.",
  steps:[
    { t:'Open Reports', b:'From the menu under Manage. Past reports stay available to download for 30 days.', hint:'Tap Reports',
      mock:{ title:'Menu', items:[
        {k:'row', ic:'chart', t:'Reports', s:'Export & schedule', hot:true},
        {k:'row', ic:'users', t:'Users', s:'Access & sharing'},
      ]}},
    { t:'Choose a report type', b:'Mileage for taxes, driving score for coaching, speeding violations for the conversation you’d rather have with data than without.', hint:'Tap Mileage report',
      mock:{ title:'Create report', back:true, items:[
        {k:'row', ic:'gauge', t:'Mileage report', s:'Distance by vehicle and day', hot:true},
        {k:'row', ic:'shield', t:'Driving score report', s:'Braking, acceleration, speed'},
        {k:'row', ic:'speed', t:'Speeding violations', s:'Every event over the limit'},
      ]}},
    { t:'Pick vehicles and dates', b:'One vehicle or the whole fleet. Common ranges — last month, last quarter — are one tap.', hint:'Tap Date range',
      mock:{ title:'Mileage report', back:true, items:[
        {k:'field', l:'Devices', v:'All 26 devices'},
        {k:'field', l:'Date range', v:'Last month', hot:true},
      ]}},
    { t:'Run it now, or every week', b:'Generate once and download, or switch on a schedule and it emails itself to whoever needs it.', hint:'Tap the schedule switch',
      mock:{ title:'Mileage report', back:true, items:[
        {k:'toggle', l:'Email this weekly', s:'Every Monday, 7:00 AM', on:false, hot:true},
        {k:'btn', l:'Generate report', kind:'primary'},
      ]}},
  ],
  end:"Finished reports appear in the list with a download link, and scheduled ones keep arriving until you switch them off."
},
/* ---------------------------------------------------------------- 10 */
{ id:'studio', title:'Give your vehicle a custom look', ic:'camera', cat:'Personalize',
  tracks:['more'], go:'studio', mins:'2 min',
  sum:'Pick a body style and colour so you can tell vehicles apart at a glance.',
  kw:'vehicle studio image icon colour color customise photo avatar body type look',
  intro:"Purely cosmetic, and worth the two minutes. When you have more than three vehicles, a red pickup and a white van are far quicker to read than two identical dots.",
  steps:[
    { t:'Open the vehicle, tap the image', b:'On the vehicle screen, the picture at the top is a button. Tap it to open Vehicle Studio.', hint:'Tap the vehicle image',
      mock:{ title:'Shop Van', items:[
        {k:'map', label:'Tap the vehicle image', hot:true},
        {k:'stat', l:'Status', v:'Moving · 34 mph'},
      ]}},
    { t:'Choose a body style', b:'Sedan, pickup, van, motorcycle, trailer and more. Pick the closest — exactness isn’t the point.', hint:'Tap Van',
      mock:{ title:'Vehicle Studio', back:true, items:[
        {k:'chips', opts:['Sedan','SUV','Van','Bike'], sel:2, hot:true},
        {k:'note', t:'Step 1 of 3 · Body style'},
      ]}},
    { t:'Pick a colour, then save', b:'Match the real vehicle if you can — it makes the map instantly readable. Save and it applies everywhere the vehicle appears.', hint:'Tap Save look',
      mock:{ title:'Vehicle Studio', back:true, items:[
        {k:'swatches', hot:true},
        {k:'btn', l:'Save look', kind:'primary'},
      ]}},
  ],
  end:"Prefer a real photo? The Image catalog on the More screen lets you upload one instead."
},
/* ---------------------------------------------------------------- 11 */
{ id:'mapprefs', title:'Change map, units & language', ic:'map', cat:'Personalize',
  tracks:['new','more'], go:'settings', mins:'2 min',
  sum:'Miles or kilometres, satellite or streets, and the language the app speaks.',
  kw:'settings units miles kilometres kph mph map satellite layers language english spanish french polylines',
  intro:"Small settings, big difference to daily use. Worth doing once, right at the start.",
  steps:[
    { t:'Settings → Map preferences', b:'This is where the map itself is configured — how often it refreshes, whether routes are drawn, which base layer you see.', hint:'Tap Map preferences',
      mock:{ title:'Settings', items:[
        {k:'row', ic:'map', t:'Map preferences', s:'Units, layers, updates', hot:true},
        {k:'row', ic:'globe', t:'Language', s:'System default'},
      ]}},
    { t:'Set your units', b:'Miles and MPH, or kilometres and KPH. Everything — trips, reports, speed alerts — follows this one choice.', hint:'Tap the units control',
      mock:{ title:'Map preferences', back:true, items:[
        {k:'seg', opts:['Miles','Kilometres'], sel:0, hot:true},
        {k:'field', l:'Map auto updates', v:'Auto'},
        {k:'field', l:'Show polylines', v:'Show'},
      ]}},
    { t:'Change the language if you’d like', b:'English, Spanish, French, German, Portuguese, Chinese and Japanese. The app switches straight away.', hint:'Tap Español',
      mock:{ title:'Language', back:true, items:[
        {k:'row', ic:'globe', t:'English', s:'Current'},
        {k:'row', ic:'globe', t:'Español', s:'Spanish', hot:true},
        {k:'row', ic:'globe', t:'Français', s:'French'},
      ]}},
  ],
  end:"That’s the housekeeping done. Everything else in the app now speaks your units and your language."
},
];

const GUIDE_CATS = ['Getting started','Alerts & zones','People & access','Everyday use','Reports & records','Personalize'];

Object.assign(window, { GUIDE_TOPICS, GUIDE_TRACKS, GUIDE_CATS });
